import { normalizeProjectionGroup } from '../../businessLogicService/normalizeProjectionGroup';
import { splitId } from '../../businessLogicService/splitIdsFromBackEnd';
import { DiagnosticsCompletedStudentsQuery$data } from '../../graphQLOperations/__generated__/DiagnosticsCompletedStudentsQuery.graphql';
import { DiagnosticsGetStudentsTableQuery$data } from '../../graphQLOperations/__generated__/DiagnosticsGetStudentsTableQuery.graphql';
import { DiagnosticsStatusByClassReportQuery$data } from '../../graphQLOperations/__generated__/DiagnosticsStatusByClassReportQuery.graphql';
import { DownloadData } from './DiagnosticReportDownloadCSV';

export enum DiagnosticStatus {
  COMPLETED = 'completed',
  IN_PROGRESS = 'in-progress',
  NOT_STARTED = 'not-started',
}
export type ClassDiagnosticStatusReport = DiagnosticsStatusByClassReportQuery$data['classDiagnosticStatusReport'];
export type StudentReport = ClassDiagnosticStatusReport[number];
export type ClassDiagnosticReport = DiagnosticsGetStudentsTableQuery$data['classDiagnosticReport'];

// Most recent diagnostic for a student, by highest backend id.
export const getLatestDiagnostic = (student: StudentReport['student']) => {
  const diagnostics = student.diagnostics;
  if (!diagnostics?.length) return null;

  return [...diagnostics]
    .filter((d): d is NonNullable<typeof d> => !!d)
    .sort((a, b) => Number(splitId(b.id)) - Number(splitId(a.id)))[0];
};

const isLatestDiagnosticReset = (student: StudentReport['student']): boolean => {
  const diagnostics = student.diagnostics;
  if (!diagnostics?.length) return false;

  const latest = diagnostics[diagnostics.length - 1];
  return latest?.retakeAssigned === true;
};

export const getStudentDiagnosticStatus = (report: StudentReport): DiagnosticStatus => {
  switch (report.diagnosticStatus) {
    case 'DIAGNOSTIC_SUBMITTED':
      // A submitted-but-reset diagnostic drops back to "not started": the old
      // result is wiped and the student has to retake it.
      return isLatestDiagnosticReset(report.student) ? DiagnosticStatus.NOT_STARTED : DiagnosticStatus.COMPLETED;
    case 'DIAGNOSTIC_STARTED':
    case 'DIAGNOSTIC_RESUMED':
      return DiagnosticStatus.IN_PROGRESS;
    // null / undefined (never launched) and any other ActionType fall through to "not started"
    default:
      return DiagnosticStatus.NOT_STARTED;
  }
};

export const filterStudentsByStatus = (
  students: ClassDiagnosticStatusReport,
  status: DiagnosticStatus,
): StudentReport[] => students.filter(report => getStudentDiagnosticStatus(report) === status);

export const getInProgressStudentIds = (students: ClassDiagnosticStatusReport): Set<string> =>
  new Set(filterStudentsByStatus(students, DiagnosticStatus.IN_PROGRESS).map(report => report.student.id));

export const excludeInProgressStudents = (
  students: DiagnosticsCompletedStudentsQuery$data,
  inProgressIds: Set<string>,
) => {
  const nodes = students.allStudents?.nodes ?? [];
  return nodes.filter(student => !!student && !inProgressIds.has(splitId(student.id as string)));
};

// Human-readable status used in the CSV export. Unlike getStudentDiagnosticStatus
// a submitted-but-reset diagnostic stays "Completed" here; the reset shows in its own column.
export const getDownloadStatusLabel = (report: StudentReport): string => {
  const latest = getLatestDiagnostic(report.student);

  if (latest && latest.retakeAssigned === true) {
    if (report.diagnosticStatus === 'DIAGNOSTIC_STARTED' || report.diagnosticStatus === 'DIAGNOSTIC_RESUMED') {
      return 'In Progress';
    } else {
      return 'Not Started';
    }
  }

  if (report.diagnosticStatus === 'DIAGNOSTIC_STARTED' || report.diagnosticStatus === 'DIAGNOSTIC_RESUMED') {
    return 'In Progress';
  }

  if (
    report.diagnosticStatus === 'DIAGNOSTIC_SUBMITTED' ||
    (report.diagnosticStatus == null && !!latest && latest.retakeAssigned === false)
  ) {
    return 'Completed';
  }

  return 'Not Started';
};

// Merge the status report (all students: status, reset, time) with the class report
// (demonstrated skills, projections and completion date) into the CSV rows.
export const buildDiagnosticDownloadData = (
  statusReport: ClassDiagnosticStatusReport,
  classReport: ClassDiagnosticReport,
): DownloadData[] =>
  statusReport.map(report => {
    const firstName = report.student.firstName ?? '';
    const lastName = report.student.lastName ?? '';
    const latest = getLatestDiagnostic(report.student);
    const reportInfo = classReport.find(el => splitId(el.student.id) === splitId(report.student.id));

    return {
      name: `${firstName} ${lastName}`,
      firstName,
      lastName,
      status: getDownloadStatusLabel(report),
      date: reportInfo?.diagnosticDate ?? null,
      timeSpent:
        report.totalActiveTime !== undefined && report.totalActiveTime !== null
          ? String(Math.round(report.totalActiveTime / 60))
          : '',
      reset: latest?.retakeAssigned === true,
      demonstrated: reportInfo?.demonstratedSkills != null ? String(reportInfo.demonstratedSkills) : '',
      projectedTwo: normalizeProjectionGroup(reportInfo?.projectedStatusTwoSkills) ?? '',
      projectedThree: normalizeProjectionGroup(reportInfo?.projectedStatusThreeSkills) ?? '',
    };
  });
